import "./Insights.scss";
import { FullPageSpinner, Page } from "../Components";
import { RouteComponentProps, Router } from "@reach/router";
import { AmcLambdaFetch, awaitJSON } from "../utils/fetch-utils";
import { useSetError } from "../redux/modals";
import { useCompanyInfo } from "../redux/company";
import { useSelector } from "react-redux";
import { useTabbedNav } from "../utils/hooks/useNav";
import * as UserRedux from "../redux/user";
import Geo from "./Pages/Geo/Geo";
import MediaMix from "./Pages/MediaMix/MediaMix";
import NewToBrand from "./Pages/NewToBrand/NewToBrand";
import PathingAnalysis from "./Pages/PathingAnalysis/PathingAnalysis";
import React, { useEffect, useMemo, useState } from "react";
import Tableau from "./Pages/Tableau/Tableau";

interface Report {
  reportType: string;
  amcInstanceId?: string;
}

const enum PageTab {
  GEO = "geo",
  MEDIA_MIX = "media-mix",
  NEW_TO_BRAND = "new-to-brand",
  PATHING_ANALYSIS = "pathing-analysis",
  TABLEAU = "tableau",
}

const NAVS = [
  { label: "Geo", key: PageTab.GEO },
  { label: "Media Mix", key: PageTab.MEDIA_MIX },
  { label: "New to Brand", key: PageTab.NEW_TO_BRAND },
  { label: "Pathing Analysis", key: PageTab.PATHING_ANALYSIS },
  { label: "Tableau", key: PageTab.TABLEAU },
];

export const AMC_TEST_INSTANCE_ID = "amcgqfnthsb"; //Drip Drop for the testing

export default React.memo(({ navigate }: RouteComponentProps) => {
  const setError = useSetError();
  const isAdmin = useSelector(UserRedux.isAdminSelector);
  const { tab, goToTab } = useTabbedNav({
    navigate,
    baseURL: "amc-insights",
    defaultKey: "",
    navs: NAVS.map(nav => nav.key, NAVS),
  });

  let { cid, amc_instance_id: amcInstanceId } = useCompanyInfo();
  if (cid === "tinuititest") {
    amcInstanceId = AMC_TEST_INSTANCE_ID;
  }

  const [data, setData] = useState<Report[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAMCData = async () => {
      if (!amcInstanceId) {
        return;
      }
      try {
        setLoading(true);
        const response = await AmcLambdaFetch("/getAmcReports", {
          params: { amcInstanceId },
        });
        const jsonData = await awaitJSON<Report[]>(response);
        setData(jsonData);
      } catch (error) {
        setError({
          title: "Error fetching AMC data",
          message: "There was an error fetching AMC data. Please try again later.",
        });
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchAMCData();
  }, [amcInstanceId, setError]);

  const availableTabs = useMemo(() => {
    const filteredTabs = NAVS.filter(tab =>
      data?.map(report => report.reportType.replaceAll("_", "-")).includes(tab.key)
    );

    // Manually add the "Tableau" tab to the filtered tabs list
    const tableauTab = NAVS.find(tab => tab.key === PageTab.TABLEAU);
    if (tableauTab && isAdmin) {
      filteredTabs.push(tableauTab);
    }

    return filteredTabs;
  }, [data, isAdmin]);

  useEffect(() => {
    if (availableTabs.length && !tab) {
      goToTab(availableTabs[0].key);
    }
  }, [availableTabs, tab, goToTab]);

  return (
    <Page
      app2Redesign
      pageType="AMC Insights"
      title="AMC Insights"
      navs={availableTabs}
      selectedNav={tab}
      onNav={goToTab}
    >
      <div className="amc-insights-page">
        {loading && (
          <div className="loading-indicator">
            <FullPageSpinner />
          </div>
        )}
        {!loading && !data && <div className="error-message">No AMC data available.</div>}
        {data && (
          <Router className="fullPageRouter">
            <Geo path={PageTab.GEO} />
            <MediaMix path={PageTab.MEDIA_MIX} />
            <NewToBrand path={PageTab.NEW_TO_BRAND} />
            <PathingAnalysis path={PageTab.PATHING_ANALYSIS} />
            {isAdmin && <Tableau path={PageTab.TABLEAU} />}
          </Router>
        )}
      </div>
    </Page>
  );
});
